import { DefaultTheme, ThemeProps } from 'styled-components';
import tinyColor from 'tinycolor2';
import { createHandler } from '@mews/std';
import { getToken } from '@optimus-web/common';
import { ButtonVariant } from './types';

export const getColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) =>
            // This logic is here to ensure that there's correct color contrast even if the primary background color is themed.
            tinyColor.isReadable(
                getToken('backgroundInteractivePrimaryDefault')({ theme }),
                getToken('textStaticInverse')({ theme })
            )
                ? getToken('textStaticInverse')({ theme })
                : getToken('textStaticPrimary')({ theme }),
        secondary: ({ theme }) => getToken('textInteractivePrimaryDefault')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getBackgroundColor = createHandler<
    ButtonVariant,
    ((props: ThemeProps<DefaultTheme>) => string) | 'transparent'
>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryDefault')({ theme }),
        secondary: 'transparent',
        tertiary: 'transparent',
        ghost: 'transparent',
        inverse: 'transparent',
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerDefault')({ theme }),
    },
    'transparent'
);

export const getBorderColor = createHandler<ButtonVariant, ((props: ThemeProps<DefaultTheme>) => string) | string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryDefault')({ theme }),
        secondary: ({ theme }) => getToken('borderInteractivePrimaryDefault')({ theme }),
        tertiary: ({ theme }) => getToken('borderInteractiveSecondaryDefault')({ theme }),
        ghost: 'transparent',
        inverse: ({ theme }) => getToken('borderStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerDefault')({ theme }),
    },
    ({ theme }) => getToken('borderInteractiveSecondaryDefault')({ theme })
);

export const getHoverColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getHoverBackgroundColor = createHandler<
    ButtonVariant,
    ((props: ThemeProps<DefaultTheme>) => string) | string
>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryHover')({ theme }),
        secondary: ({ theme }) => getToken('backgroundInteractivePrimaryHover')({ theme }),
        tertiary: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleHover')({ theme }),
        ghost: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleHover')({ theme }),
        inverse: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleHover')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerHover')({ theme }),
    },
    ({ theme }) => getToken('backgroundInteractiveNeutralSubtleHover')({ theme })
);

export const getHoverBorderColor = createHandler<ButtonVariant, ((props: ThemeProps<DefaultTheme>) => string) | string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryHover')({ theme }),
        secondary: ({ theme }) => getToken('borderInteractivePrimaryHover')({ theme }),
        tertiary: ({ theme }) => getToken('borderInteractiveSecondaryHover')({ theme }),
        ghost: 'transparent',
        inverse: ({ theme }) => getToken('borderStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerHover')({ theme }),
    },
    ({ theme }) => getToken('borderInteractiveSecondaryHover')({ theme })
);

export const getDisabledColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textDisabled')({ theme }),
        secondary: ({ theme }) => getToken('textDisabled')({ theme }),
        tertiary: ({ theme }) => getToken('textDisabled')({ theme }),
        ghost: ({ theme }) => getToken('textDisabled')({ theme }),
        inverse: ({ theme }) => getToken('textDisabled')({ theme }),
        destructive: ({ theme }) => getToken('textDisabled')({ theme }),
    },
    ({ theme }) => getToken('textDisabled')({ theme })
);

export const getDisabledBackgroundColor = createHandler<
    ButtonVariant,
    ((props: ThemeProps<DefaultTheme>) => string) | string
>(
    {
        primary: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        secondary: 'transparent',
        tertiary: 'transparent',
        ghost: 'transparent',
        inverse: 'transparent',
        destructive: ({ theme }) => getToken('backgroundDisabled')({ theme }),
    },
    'transparent'
);

export const getDisabledBorderColor = createHandler<
    ButtonVariant,
    ((props: ThemeProps<DefaultTheme>) => string) | string
>(
    {
        primary: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        secondary: ({ theme }) => getToken('borderDisabled')({ theme }),
        tertiary: ({ theme }) => getToken('borderDisabled')({ theme }),
        ghost: 'transparent',
        inverse: ({ theme }) => getToken('borderStaticTertiary')({ theme }),
        destructive: ({ theme }) => getToken('backgroundDisabled')({ theme }),
    },
    ({ theme }) => getToken('borderDisabled')({ theme })
);

export const getActiveColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('backgroundDisabled')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getActiveBackgroundColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryActive')({ theme }),
        secondary: ({ theme }) => getToken('backgroundInteractivePrimaryActive')({ theme }),
        tertiary: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleActive')({ theme }),
        ghost: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleActive')({ theme }),
        inverse: ({ theme }) => getToken('backgroundInteractiveNeutralSubtleActive')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerActive')({ theme }),
    },
    ({ theme }) => getToken('backgroundInteractiveNeutralSubtleActive')({ theme })
);

export const getActiveBorderColor = createHandler<
    ButtonVariant,
    ((props: ThemeProps<DefaultTheme>) => string) | string
>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryActive')({ theme }),
        secondary: ({ theme }) => getToken('borderInteractivePrimaryActive')({ theme }),
        tertiary: ({ theme }) => getToken('borderInteractiveSecondaryActive')({ theme }),
        ghost: 'transparent',
        inverse: ({ theme }) => getToken('borderStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerActive')({ theme }),
    },
    ({ theme }) => getToken('borderInteractiveSecondaryActive')({ theme })
);

export const getCounterColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryDefault')({ theme }),
        secondary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        tertiary: ({ theme }) => getToken('backgroundStaticFlat')({ theme }),
        ghost: ({ theme }) => getToken('textStaticInverse')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerDefault')({ theme }),
    },
    ({ theme }) => getToken('backgroundStaticFlat')({ theme })
);

export const getCounterBackgroundColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('textInteractivePrimaryDefault')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getCounterDisabledColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        secondary: ({ theme }) => getToken('textDisabled')({ theme }),
        tertiary: ({ theme }) => getToken('textDisabled')({ theme }),
        ghost: ({ theme }) => getToken('textDisabled')({ theme }),
        inverse: ({ theme }) => getToken('textDisabled')({ theme }),
        destructive: ({ theme }) => getToken('backgroundDisabled')({ theme }),
    },
    ({ theme }) => getToken('textDisabled')({ theme })
);

export const getCounterDisabledBackgroundColor = createHandler<
    ButtonVariant,
    (props: ThemeProps<DefaultTheme>) => string
>(
    {
        primary: ({ theme }) => getToken('textDisabled')({ theme }),
        secondary: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        tertiary: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        ghost: ({ theme }) => getToken('backgroundDisabled')({ theme }),
        inverse: ({ theme }) => getToken('backgroundStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textDisabled')({ theme }),
    },
    ({ theme }) => getToken('backgroundDisabled')({ theme })
);

export const getCounterActiveColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryActive')({ theme }),
        secondary: ({ theme }) => getToken('backgroundInteractivePrimaryActive')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        ghost: ({ theme }) => getToken('textStaticInverse')({ theme }),
        inverse: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerActive')({ theme }),
    },
    ({ theme }) => getToken('backgroundInteractiveNeutralSubtleActive')({ theme })
);

export const getCounterActiveBackgroundColor = createHandler<
    ButtonVariant,
    (props: ThemeProps<DefaultTheme>) => string
>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('backgroundStaticFlat')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getCounterHoverColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('backgroundInteractivePrimaryHover')({ theme }),
        secondary: ({ theme }) => getToken('backgroundInteractivePrimaryHover')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        ghost: ({ theme }) => getToken('textStaticInverse')({ theme }),
        inverse: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        destructive: ({ theme }) => getToken('backgroundInteractiveDangerHover')({ theme }),
    },
    ({ theme }) => getToken('backgroundInteractiveNeutralSubtleHover')({ theme })
);

export const getCounterHoverBackgroundColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('backgroundStaticFlat')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticPrimary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);

export const getLoaderIconColor = createHandler<ButtonVariant, (props: ThemeProps<DefaultTheme>) => string>(
    {
        primary: ({ theme }) => getToken('textStaticInverse')({ theme }),
        secondary: ({ theme }) => getToken('textInteractivePrimaryDefault')({ theme }),
        tertiary: ({ theme }) => getToken('textStaticSecondary')({ theme }),
        ghost: ({ theme }) => getToken('textStaticSecondary')({ theme }),
        inverse: ({ theme }) => getToken('textStaticInverse')({ theme }),
        destructive: ({ theme }) => getToken('textStaticInverse')({ theme }),
    },
    ({ theme }) => getToken('textStaticSecondary')({ theme })
);
